/**
 * ActiveSessionPage — thin wrapper around the existing charging wizard
 * in src/App.jsx. All state and bootstrap-recovery live inside App
 * (SessionContext, useStationStream, beforeunload cancel etc.).
 *
 * The only responsibility here is to record the finalised session
 * into the per-user local history cache exactly once when the
 * wizard reaches SUMMARY (dedupe via a per-sessionUid Set).
 *
 * The source-aware Back button that used to live here as a floating
 * pill has been moved INSIDE the wizard's EvHeader so it sits in
 * the charger-profile header rather than as a page-level control.
 * See EvHeader.jsx for the current implementation.
 */

import React, { useEffect, useRef } from 'react';
import App from '../../App.jsx';
import { useSession } from '../../context/SessionContext.jsx';
import { useAuth } from '../../context/AuthContext.jsx';
import { STEPS } from '../../constants/steps.js';
import { recordSessionFromContext } from '../../utils/sessionRecord.js';

function useRecordSessionOnSummary() {
  const { user } = useAuth();
  const session = useSession();
  const recorded = useRef(new Set());
  const state = session?.state;
  const step = state?.step;
  const sessionUid = state?.sessionUid;

  useEffect(() => {
    if (!user?.id || !sessionUid) return;
    if (step !== STEPS.SUMMARY) return;
    if (recorded.current.has(sessionUid)) return;
    recorded.current.add(sessionUid);
    try {
      recordSessionFromContext(user.id, state);
    } catch { /* swallow — cache write is best-effort */ }
  }, [user?.id, sessionUid, step, state]);
}

export default function ActiveSessionPage() {
  useRecordSessionOnSummary();
  return <App />;
}
